/**
 * Merge rules for the durable call-info fields (case number, phone, PIN).
 *
 * Supabase is the durable source of truth for these fields, but the operator can
 * be holding an edit that has not been flushed yet. `useCallInfoSync` reloads the
 * row on mount and after a reconnect, so it needs a per-field rule for "whose
 * value wins" — that rule is subtle enough (and has been wrong twice) to live in
 * one tested place instead of inline in the hook.
 *
 * The rules:
 *
 *  1. With a baseline (we have loaded or written before): a field whose LOCAL
 *     value differs from the baseline is an un-flushed operator edit and wins.
 *     Every other field takes the DB value, so a peer's concurrent write — or
 *     clear — of an untouched field is honoured rather than clobbered.
 *     (#725-followup/F1: the old all-or-nothing guard skipped the whole reload
 *     whenever ANY field was dirty, and the writer then re-wrote the stale
 *     companions over the peer's change.)
 *
 *  2. Without a baseline (the FIRST load): a local value is kept ONLY when it is
 *     an explicitly declared, not-yet-persisted handoff AND the DB has nothing
 *     for that field. This is the pre-join case number (#945): CustomPreJoin
 *     writes it to Redux before the room is joined, the calls row is created
 *     without a case_number, and applying the DB row wholesale replaced it with
 *     '' — so it vanished from the active-call header.
 *
 *     The handoff must be DECLARED, not inferred from "local is truthy": Redux
 *     call-info is not cleared between mounts, so a stale value from an earlier
 *     call would otherwise look like an un-flushed edit and could resurrect a
 *     field a peer had deliberately cleared. A declared handoff has provenance;
 *     truthiness does not.
 *
 * A field reported dirty has NOT been persisted yet, so the caller must flush it.
 */

export interface CallInfoFields {
  incidentNumber: string | null;
  phoneNumber: string | null;
  phonePin: string | null;
}

export type CallInfoField = keyof CallInfoFields;

export interface CallInfoMergeResult {
  /** The values to apply to Redux. */
  merged: CallInfoFields;
  /** Fields held locally that the DB does not agree with — the caller must flush these. */
  dirty: Record<CallInfoField, boolean>;
  /** True when any field is dirty (i.e. a write is owed). */
  hasUnflushed: boolean;
}

const FIELDS: CallInfoField[] = ['incidentNumber', 'phoneNumber', 'phonePin'];

export function mergeCallInfoFromDb(args: {
  /** What we believe is persisted; null before the first load/write. */
  baseline: CallInfoFields | null;
  /** Current Redux state. */
  local: CallInfoFields;
  /** The row we just read. */
  remote: CallInfoFields;
  /**
   * Values handed to this call by an earlier step that has NOT persisted them
   * yet — today only the pre-join case number. A field is protected on the
   * first load only when the local value still equals its declared handoff.
   */
  pendingHandoff?: Partial<CallInfoFields> | null;
}): CallInfoMergeResult {
  const { baseline, local, remote, pendingHandoff } = args;

  const merged = {} as CallInfoFields;
  const dirty = {} as Record<CallInfoField, boolean>;

  for (const field of FIELDS) {
    const handoff = pendingHandoff?.[field] ?? null;
    const isDirty = baseline !== null
      ? local[field] !== baseline[field]
      // First load: keep only a declared handoff the DB has not got yet.
      : Boolean(local[field]) && !remote[field] && local[field] === handoff;

    dirty[field] = isDirty;
    merged[field] = isDirty ? local[field] : remote[field];
  }

  return {
    merged,
    dirty,
    hasUnflushed: FIELDS.some((field) => dirty[field]),
  };
}
